import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Container,
  ContentLayout,
  Header,
  SpaceBetween,
  Spinner,
} from "@cloudscape-design/components";
import { apiFetch } from "./apiFetch";
import { parseView, type View } from "./dynamicPages/viewContract";
import { WidgetRenderProvider, renderWidget } from "./dynamicPages/widgetRegistry";
import { fetchContentTypes, type SchemaContentType } from "./dynamicPages/schemaApi";
import { useDocumentTitle } from "./useDocumentTitle";

/**
 * Runtime renderer for a user-defined page, mounted at the catch-all route `/p/:slug`.
 *
 * DynamicPage is a THIN HOST implementing render-pipeline stages 1-3 (see
 * widgetRegistry.tsx for the full 4-stage contract):
 *   (1) load the `page` by slug and parseView() its `view` JSON (structural only);
 *   (2) load the content-type schema ONCE and provide it to widgets via
 *       WidgetRenderProvider (context);
 *   (3) render each widget IN ORDER through the registry with a STABLE key, each in its
 *       own Container.
 * It fetches NO collection data itself — each widget component (stage 4) owns its data
 * lifecycle, scoped loading/error, pagination and filters.
 *
 * Validation is RENDER-TIME and SCOPED: a malformed/non-conforming view or a failed
 * schema load produce a page-level Alert; an unknown widget type (or a widget whose own
 * data fetch fails) produces a scoped Alert in that widget's slot while every other
 * widget still renders.
 */

interface PageRecord {
  title: string;
  slug: string;
  view: unknown;
}

export default function DynamicPage() {
  const { slug } = useParams<{ slug: string }>();

  // Read the URL query string ONCE here (the thin host is the single place that touches
  // the URL). Build a plain map of the CURRENTLY PRESENT params (name -> value) and hand
  // it to widgets via context; widgets never parse the URL themselves. A new map is
  // produced whenever the query string changes, so param-bound widgets refetch.
  const [searchParams] = useSearchParams();
  const urlParams = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      map[key] = value;
    });
    return map;
    // Re-derive only when the serialised query string changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [title, setTitle] = useState<string>("");
  useDocumentTitle(title || slug);
  const [view, setView] = useState<View | null>(null);
  const [schema, setSchema] = useState<SchemaContentType[]>([]);

  // Load the page DEFINITION: page record + parsed view + schema (loaded once). Keyed on
  // `slug` only. No data fetching here — widgets fetch their own.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setPageError(null);
      setView(null);
      setSchema([]);

      if (!slug) {
        setPageError("No page slug provided.");
        setLoading(false);
        return;
      }

      try {
        // 1. Fetch the page by slug.
        const pageRes = await apiFetch(`/pages?filters[slug][$eq]=${encodeURIComponent(slug)}`);
        if (!pageRes.ok) throw new Error(`Failed to load page (${pageRes.status})`);
        const pageJson = await pageRes.json();
        const record: PageRecord | undefined = pageJson?.data?.[0];
        if (!record) {
          setPageError(`No page found for "/p/${slug}".`);
          setLoading(false);
          return;
        }
        if (!cancelled) setTitle(record.title || slug);

        // 2. Parse the view JSON (structural validation).
        const raw =
          typeof record.view === "string" ? record.view : JSON.stringify(record.view ?? {});
        const parsed = parseView(raw);
        if (!parsed.ok) {
          if (!cancelled) {
            setPageError(`The view for this page is invalid: ${parsed.error}`);
            setLoading(false);
          }
          return;
        }

        // 3. Load the content-type schema ONCE (provided to all widgets via context).
        let loadedSchema: SchemaContentType[] = [];
        try {
          loadedSchema = await fetchContentTypes();
        } catch (e) {
          if (!cancelled) {
            setPageError(`Failed to load content-type schema: ${(e as Error).message}`);
            setLoading(false);
          }
          return;
        }

        if (!cancelled) {
          setView(parsed.view);
          setSchema(loadedSchema);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setPageError((e as Error).message);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (pageError) {
    return (
      <ContentLayout header={<Header variant="h1">{title || "Page"}</Header>}>
        <Alert type="error" header="This page cannot be displayed">
          {pageError}
        </Alert>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout header={<Header variant="h1">{title}</Header>}>
      {view && (
        <WidgetRenderProvider schema={schema} urlParams={urlParams}>
          <SpaceBetween size="l">
            {view.widgets.map((widget, index) => (
              // Each top-level widget sits in its own Container. renderWidget applies the
              // STABLE `${type}-${index}` key and handles unknown types with a scoped Alert.
              <Container key={`${(widget as { type: string }).type}-${index}`}>
                {renderWidget(widget, index)}
              </Container>
            ))}
          </SpaceBetween>
        </WidgetRenderProvider>
      )}
    </ContentLayout>
  );
}
